// Components
const ModalCancelOrder = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "modal-cancel-order"*/ "@/app/views/main-view/dog-purchase/containers/dog-transaction-history-container/modals/modal-cancel-order/modal-cancel-order.vue"
    );
const ProductItemCard = () =>
    import(
        /* webpackPrefetch: true, webpackChunkName: "product-item-card" */ "@/app/views/main-view/_shared/_components/product-item-card/product-item-card.vue"
    );

// Services
import SubscriptionService from "@/app/services/main/subscription/subscription-service";

// Mixins
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins";
import { dateMixins } from "@/app/mixins/utils/date-mixins";
import { mobileNumberMixins } from "@/app/mixins/utils/mobile-number-mixins";
import { userMixins } from "@/app/mixins/modules/user-mixins"

// Constants
import { STATUS_CODES, CANCELLATION_TYPE} from "@/app/constants/status-constants";
import { SUBSCRIPTION_STATUS } from "@/app/constants/subscription-constants";

export default {
    name: "SubscriptionDetailsContainer",
    components: {
        ModalCancelOrder,
        ProductItemCard,
    },
    directive: {},
    filters: {},
    mixins: [
        toastMessageMixins,
        dateMixins,
        mobileNumberMixins,
        userMixins,
    ],
    props: {},
    data() {
        return {
            // API
            isAPILoading: false,

            // Subscription Details
            subscriptionId: null,
            subscriptionStatus: "",
            subscriptionDetails: {},
            subtotal: 0,

            customerDetails: {
                recipientName: "",
                petName: "",
                email: "",
                shippingAddress: "",
            },

            transactionDetails: {
                frequency: "",
                paymentMethod: "",
                trackingNumber: "",
            },

            // Transaction Timeline
            transactionHistory: [],

            orderedProducts: [],

            // Payment Details
            paymentDetails: [],

            theadsPayment: [
                {
                    name: "Payment Method",
                    sortLabel: null,
                    isDesc: false,
                    style: null,
                },
                { name: "Status", sortLabel: null, isDesc: false, style: null },
                {
                    name: "Message",
                    sortLabel: null,
                    isDesc: false,
                    style: null,
                },
                { name: "Price", sortLabel: null, isDesc: false, style: null },
            ],

            cancellationTitle: "Cancel Subscription",
			cancellationText: "Are you sure you want to cancel this subscription? Please state your reason in the textbox below.",
            cancellationType: CANCELLATION_TYPE.SUBSCRIPTION_CANCELLATION,
            previousRouteLabel: 'Subscriptions' 
        };
    },
    computed: {
        showCancelSubscriptionOption() {
            const negativeStatuses =  [SUBSCRIPTION_STATUS.CANCELLED, SUBSCRIPTION_STATUS.FOR_CANCELLATION, SUBSCRIPTION_STATUS.ENDED];
            return !negativeStatuses.includes(this.subscriptionStatus);
        },
        statusColor() {
            const statusMap = {
                ["ACTIVE"]: "status--active",
                [SUBSCRIPTION_STATUS.CANCELLED]: "status--red",
                [SUBSCRIPTION_STATUS.ENDED]: "status--red",
                [SUBSCRIPTION_STATUS.DELETED]: "status--red",
                [SUBSCRIPTION_STATUS.FOR_CANCELLATION]: "status--cancelled",
            };

            return statusMap[this.subscriptionStatus] || "status--grey";
        },
        timelineItems() {
            let items = [];
            if (this.transactionHistory && this.transactionHistory.length > 0) {
                for (const data of this.transactionHistory) {
                    const { transactionDate, status, remarks, submittedBy } = data;

                    let transDate = this.formatDateTime(transactionDate);
                    let content = SUBSCRIPTION_STATUS[status] ? SUBSCRIPTION_STATUS[status].replace("_", " ") : "";

                    if (status === SUBSCRIPTION_STATUS.FOR_CANCELLATION) {
                        content += `<br> <p class="color-warning mb-0">Reason: ${remarks}</p>`;
                    }
                    else if ((status === SUBSCRIPTION_STATUS.CANCELLED || status === SUBSCRIPTION_STATUS.APPROVED_CANCELLATION || status === SUBSCRIPTION_STATUS.DISAPPROVED_CANCELLATION) && submittedBy) {
                        content += `<br> <p class="color-warning mb-0">Remarks: ${remarks}</p>`;
                        content += `<p class="color-warning mb-0">Verified By: ${submittedBy}</p>`
                    }
                    else{
                        content += `<br> <p class="color-warning mb-0">Remarks: ${remarks}</p>`;
                    }
                    items.push({
                        htmlMode: true,
                        content: `[${transDate}] : ${content}`,
                    });
                }
            }

            return items.reverse();
        },
    },
    watch: {
        $route: {
            // this is needed if we are selecting other subscriptions from the notif component, there wont be any rerender unless we add the watcher here
            async handler() {
                this.checkRoute();
            },
            deep: true,
        },
    },
    async created() {
        if(this.$route.query.fromAction) {
            this.previousRouteLabel = 'Product Details'
        }
        this.checkRoute();
    },
    methods: {
        async checkRoute() {
            const { subscriptionId } = this.$route.params;
            this.subscriptionId = subscriptionId;
            await this.getSubscriptionDetails(subscriptionId);
		},
        async getSubscriptionDetails(subscriptionId) {
            try {
                this.isAPILoading = true;

                const petId = this.getActivePet.id

                const response = await SubscriptionService.getSubscriptionDetails(petId, subscriptionId);

                if (response.status === STATUS_CODES.SUCCESS) {
                    const data = response.data;

                    const {
                        petname: petName,
                        orderDetails,
                        status,
                        transactionDetails,
                        createdAt,
                        subscriptionExpiry,
                        transactionHistory,
                        bundles,
                        paymentDetails,
                        deliveryFee,
                        totalPrice
                    } = data;

                    this.subscriptionDetails = data;

                    this.subscriptionStatus = SUBSCRIPTION_STATUS[status];

                    // Customer Details
                    this.customerDetails = { ...orderDetails, petName };

                    // Transaction Details
                    this.transactionDetails = {
                        ...transactionDetails,
                        createdAt,
                        subscriptionExpiry,
                        deliveryFee,
                        totalPrice
                    };

                    // Timeline History
                    this.transactionHistory = transactionHistory;

                    // Payment Table
                    this.paymentDetails = paymentDetails;

                    // TODO: update implementation to only expect a single bundle from API
                    // Orders Table
                    this.orderedProducts = bundles && bundles.length ? [bundles[0]] : [];

                    this.subtotal = deliveryFee ? parseFloat(totalPrice) - parseFloat(deliveryFee) : totalPrice;
                } else {
                    this.showGenericErrorToast();
                }

                this.isAPILoading = false;
            } catch (err) {
                this.isAPILoading = false;
                this.showGenericErrorToast();
            }
        },
        showCancelOrderModal() {
			this.$refs.modalCancelOrder.openModal(this.subscriptionDetails || [])
		},
		orderedCancelled() {
            const { subscriptionId } = this.$route.params;
            this.subscriptionId = subscriptionId;

			this.getSubscriptionDetails(subscriptionId);
			this.closeCancelOrderModal();
		},
		closeCancelOrderModal() {
			$(this.$refs.modalCancelOrder).off("hide.bs.modal", this.handleHideEvent)
		},
    },
    // third party libraries
};
